/**
 * Translating a layer's filter descriptor to and from the shape the `FilterSpec*` components read.
 */
import {toActiveFilter} from './layerFilters';
import type {LayerFilter, LayerFilterValue} from './layerFilters';
import {describeAppliedFilter} from '../appliedFilters';
import type {FilterItem, SpecFilter} from '../types';

/**
 * @param filter The descriptor from the layer config.
 * @param value The user's current value for it.
 */
const toSpecFilter = (filter: LayerFilter, value: LayerFilterValue = {}): SpecFilter => {
  const spec: SpecFilter = {label: filter.label, type: filter.type, suffix: filter.suffix};
  const options = filter.options ?? [];

  if (filter.type === 'range') {
    spec.minValue = value.min ?? null;
    spec.maxValue = value.max ?? null;
    // The descriptor's default only applies until the user has said otherwise.
    spec.includeBlank = value.includeBlank ?? filter.includeBlank ?? false;
    return spec;
  }
  if (filter.type === 'text') {
    spec.searchName = value.text ?? null;
    return spec;
  }
  if (filter.type === 'chips') {
    spec.items = options.map(toItem);
    spec.selected = (value.selected ?? []).map(toItem);
    return spec;
  }
  const selected = value.selected ?? [];
  spec.options = options.map((option) => ({label: option, selected: selected.includes(option)}));
  return spec;
};

/** The value to persist, read back off what the component emitted. */
const fromSpecFilter = (filter: LayerFilter, spec: SpecFilter): LayerFilterValue => {
  if (filter.type === 'range') {
    return {
      min: toNumber(spec.minValue),
      max: toNumber(spec.maxValue),
      includeBlank: spec.includeBlank ?? false,
    };
  }
  if (filter.type === 'text') return {text: spec.searchName ?? ''};
  if (filter.type === 'chips') return {selected: (spec.selected ?? []).map((item) => item.title)};
  return {
    selected: (spec.options ?? [])
      .filter((option) => option.selected)
      .map((option) => option.label),
  };
};

/** A layer filter's chip values are their own ids — there is nothing opaque to look up. */
const toItem = (value: string): FilterItem => ({id: value, title: value});

/**
 * The inputs are text, so a bound arrives as a string and an emptied one as `''`. Both have to
 * become numbers or null, or a range compares a string against a number and matches nothing.
 */
const toNumber = (value: number | string | null | undefined): number | null => {
  if (value == null || value === '') return null;
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
};

/** Whether the user has set this filter at all. The same test the render path applies. */
const isFilterActive = (filter: LayerFilter, value: LayerFilterValue = {}): boolean =>
  toActiveFilter(filter, value) !== null;

/** What the filter's row shows for its current value. */
const describeFilterValue = (filter: LayerFilter, value: LayerFilterValue = {}): string =>
  isFilterActive(filter, value) ? describeAppliedFilter(toSpecFilter(filter, value)) : '---';

export {
  describeFilterValue,
  fromSpecFilter,
  isFilterActive,
  toSpecFilter,
};
